import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { errorMessage } from "../utils/api";
import { API_URL } from "../utils/apiUrl";
import QRForm from "../components/QRForm";
import { dayKey, DEVICE_LABELS, formatDateTime, formatNumber } from "../utils/format";

const RANGES = [
  { days: 7, label: "7 gün" },
  { days: 30, label: "30 gün" },
  { days: 90, label: "90 gün" },
  { days: 365, label: "1 yıl" },
];
const PAGE_SIZE = 50;

export default function QRDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [qr, setQr] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);

  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [scans, setScans] = useState(null);
  const [page, setPage] = useState(1);
  const [withBots, setWithBots] = useState(false);

  // The range is fixed when it is picked, so paging doesn't shift it
  const range = useMemo(() => {
    const to = Date.now();
    return { from: to - days * 24 * 3600 * 1000, to };
  }, [days]);

  useEffect(() => {
    api
      .get(`/qrs/${id}`)
      .then((res) => setQr(res.data))
      .catch((err) => setError(errorMessage(err)));
  }, [id]);

  useEffect(() => {
    setStats(null);
    api
      .get(`/qrs/${id}/stats`, { params: range })
      .then((res) => setStats(res.data))
      .catch((err) => setError(errorMessage(err)));
  }, [id, range]);

  const loadScans = useCallback(() => {
    api
      .get(`/qrs/${id}/scans`, { params: { ...range, page, limit: PAGE_SIZE, bots: withBots ? 1 : 0 } })
      .then((res) => setScans(res.data))
      .catch((err) => setError(errorMessage(err)));
  }, [id, range, page, withBots]);

  useEffect(loadScans, [loadScans]);

  // Days with no scans are shown as 0, so the line doesn't skip them
  const daily = useMemo(() => {
    if (!stats) return [];
    const counts = Object.fromEntries(stats.byDay.map((d) => [d.key, d.count]));
    const out = [];
    for (let t = range.from; t <= range.to; t += 24 * 3600 * 1000) {
      const key = dayKey(new Date(t));
      out.push({ day: key.slice(5).split("-").reverse().join("."), count: counts[key] || 0 });
    }
    return out;
  }, [stats, range]);

  async function handleSave(values) {
    try {
      const { data } = await api.put(`/qrs/${id}`, values);
      setQr(data);
      setEditing(false);
    } catch (err) {
      throw new Error(errorMessage(err));
    }
  }

  async function handleDelete() {
    if (!window.confirm(`"${qr.name}" ve tüm okutma kayıtları silinsin mi? Basılmış QR kodlar çalışmaz hale gelir.`)) {
      return;
    }
    try {
      await api.delete(`/qrs/${id}`);
      navigate("/qrs", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function handleExport() {
    try {
      const res = await api.get(`/qrs/${id}/scans.csv`, {
        params: { ...range, bots: withBots ? 1 : 0 },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scans-${id}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  function copyShortUrl() {
    navigator.clipboard?.writeText(qr.shortUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  if (!qr) {
    return error ? <div className="error">{error}</div> : <p className="muted">Yükleniyor…</p>;
  }

  const image = (format, extra = "") => `${API_URL}/qrs/${id}/image?format=${format}${extra}`;
  const pages = scans ? Math.max(1, Math.ceil(scans.total / PAGE_SIZE)) : 1;

  return (
    <>
      <Link to="/qrs" className="back">
        ← QR Kodlar
      </Link>
      <div className="page-head">
        <h1>
          {qr.name}{" "}
          <span className={`badge ${qr.active ? "badge-on" : "badge-off"}`}>{qr.active ? "Aktif" : "Pasif"}</span>
        </h1>
      </div>
      {error && <div className="error">{error}</div>}

      <div className="detail-grid">
        <div className="card qr-card">
          <img src={image("png", "&size=512")} alt={`QR: ${qr.shortUrl}`} />
          <div className="mono qr-url">
            {qr.shortUrl}
            <button className="btn btn-small" onClick={copyShortUrl}>
              {copied ? "Kopyalandı" : "Kopyala"}
            </button>
          </div>
          <div className="form-actions">
            <a className="btn" href={image("png", "&size=1024&download=1")}>
              PNG indir
            </a>
            <a className="btn" href={image("svg", "&download=1")}>
              SVG indir
            </a>
          </div>
        </div>

        <div className="card">
          {editing ? (
            <QRForm initial={qr} submitLabel="Kaydet" onSubmit={handleSave} onCancel={() => setEditing(false)} />
          ) : (
            <dl className="info">
              <dt>Hedef URL</dt>
              <dd>
                <a href={qr.targetUrl} target="_blank" rel="noreferrer">
                  {qr.targetUrl}
                </a>
              </dd>
              <dt>Oluşturulma</dt>
              <dd>{formatDateTime(qr.createdAt)}</dd>
              <dt>Son güncelleme</dt>
              <dd>{formatDateTime(qr.updatedAt)}</dd>
            </dl>
          )}
          {!editing && (
            <div className="form-actions">
              <button className="btn btn-primary" onClick={() => setEditing(true)}>
                Düzenle
              </button>
              <button className="btn btn-danger" onClick={handleDelete}>
                Sil
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="page-head">
        <h2>İstatistikler</h2>
        <div className="segmented">
          {RANGES.map((r) => (
            <button
              key={r.days}
              className={r.days === days ? "active" : ""}
              onClick={() => {
                setDays(r.days);
                setPage(1);
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!stats ? (
        <p className="muted">Yükleniyor…</p>
      ) : (
        <>
          <div className="stat-row">
            <Stat label="Okutma" value={stats.total} />
            <Stat label="Tekil ziyaretçi" value={stats.unique} />
            <Stat label="Bot / önizleme (hariç)" value={stats.bots} />
          </div>

          <div className="card">
            <h3>Günlük okutma</h3>
            <div style={{ height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={daily} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 12 }} minTickGap={16} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => [v, "Okutma"]} />
                  <Area type="monotone" dataKey="count" stroke="#2563eb" fill="#2563eb" fillOpacity={0.15} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="breakdowns">
            <Breakdown title="Ülke" rows={stats.byCountry} />
            <Breakdown title="Şehir" rows={stats.byCity} />
            <Breakdown title="Cihaz" rows={stats.byDevice} labels={DEVICE_LABELS} />
            <Breakdown title="İşletim sistemi" rows={stats.byOs} />
            <Breakdown title="Tarayıcı" rows={stats.byBrowser} />
          </div>
        </>
      )}

      <div className="page-head">
        <h2>Okutma kayıtları</h2>
        <div className="form-actions">
          <label className="check">
            <input
              type="checkbox"
              checked={withBots}
              onChange={(e) => {
                setWithBots(e.target.checked);
                setPage(1);
              }}
            />
            Botları göster
          </label>
          <button className="btn" onClick={handleExport}>
            CSV indir
          </button>
        </div>
      </div>

      {scans && (
        <div className="card table-card">
          <table>
            <thead>
              <tr>
                <th>Zaman</th>
                <th>Konum</th>
                <th>Operatör</th>
                <th>Cihaz</th>
                <th>İşletim sistemi</th>
                <th>Tarayıcı</th>
                <th>Dil</th>
              </tr>
            </thead>
            <tbody>
              {scans.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted">
                    Bu aralıkta okutma yok.
                  </td>
                </tr>
              )}
              {scans.items.map((s, i) => (
                <tr key={i} title={s.userAgent}>
                  <td>{formatDateTime(s.at)}</td>
                  <td title={s.geoStatus || undefined}>
                    {[s.city, s.country].filter(Boolean).join(", ") || (s.geoStatus === "pending" ? "Aranıyor…" : "—")}
                  </td>
                  <td>{s.isp || "—"}</td>
                  <td>{DEVICE_LABELS[s.deviceType] || s.deviceType}</td>
                  <td>{s.os || "—"}</td>
                  <td>{[s.browser, s.browserVersion].filter(Boolean).join(" ") || "—"}</td>
                  <td>{s.language || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {pages > 1 && (
            <div className="pager">
              <button className="btn btn-small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                ‹ Önceki
              </button>
              <span>
                {page} / {pages}
              </span>
              <button className="btn btn-small" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                Sonraki ›
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card stat">
      <div className="stat-value">{formatNumber(value)}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// A top-N list with a bar under each row, relative to the largest
function Breakdown({ title, rows, labels }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="card">
      <h3>{title}</h3>
      {rows.length === 0 && <p className="muted">Veri yok</p>}
      <ul className="bars">
        {rows.map((r) => (
          <li key={r.key}>
            <div className="bar-label">
              <span>{(labels && labels[r.key]) || r.key || "Bilinmiyor"}</span>
              <span className="num">{formatNumber(r.count)}</span>
            </div>
            <div className="bar">
              <div style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
